/**
 * 1.5.16 — one agreement, read and signed by typed name: the text in full (the person's language where the text
 * exists in it), Read aloud, the typed name, the "I agree" tick, then the press. Two places use it and nothing else
 * signs differently: "Complete it for them" on a person's card (staff read it to the person: "read to … by <staff>",
 * the date, the device line) and the tenant's own page from the email's link.
 */
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AGREEMENT_TEXT, agreementLang, type CardAgreement, type OnBehalfInput } from '@shared/agreement-cards';
import { MarkdownText, sha256Text } from '@/lib/docs';
import { PaperText, paperDate } from '@/components/PaperText';
import { ReadAloud } from '@/components/ReadAloud';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';

export interface AgreementSignProps {
  kind: CardAgreement;
  lang: 'en' | 'es';
  /** At the desk: whom it is read to, and by whom. On the tenant's own page: nothing (they sign for themselves). */
  desk?: { name: string; staff: string };
  busy: boolean;
  error: string | null;
  onSign: (input: OnBehalfInput) => void;
}

export function AgreementSign({ kind, lang: start, desk, busy, error, onSign }: AgreementSignProps) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const uid = useId();
  const doc = AGREEMENT_TEXT[kind];
  const [lang, setLang] = useState<'en' | 'es'>(agreementLang(kind, start));
  const [agree, setAgree] = useState(false);
  const [typed, setTyped] = useState('');
  const [problem, setProblem] = useState<'agree' | 'name' | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const agreeRef = useRef<HTMLInputElement>(null);
  const own = !desk; // 1.8.12: the tenant's own page — the text flows in the page (no scroll box inside it) and a link goes to the signature
  const text = doc.text(lang);
  const title = t(`agr.name.${kind}`);
  const date = new Intl.DateTimeFormat(i18n.language === 'es' ? 'es-US' : 'en-US', { dateStyle: 'long', timeZone: 'America/New_York' }).format(new Date());

  const sign = async () => {
    const miss = typed.trim().length < 2 ? 'name' : !agree ? 'agree' : null;
    setProblem(miss);
    if (miss) {
      announce(t(miss === 'name' ? 'agr.needName' : 'agr.needAgree'), 'assertive');
      (miss === 'name' ? nameRef : agreeRef).current?.focus(); // 1.8.12: focus goes to what is missing
      return;
    }
    try {
      onSign({ typedName: typed.trim(), agree: true, hash: doc.fixedHash ?? (await sha256Text(text)), lang });
    } catch {
      // 1.8.12: never a silent press — say it did not go through (the sheet or page keeps what was typed)
      announce(t('agr.link.failed'), 'assertive');
    }
  };

  // 1.8.12: "Go to the signature" — focus straight to the name box (a plain #sign link is the fallback without script)
  const goSign = (e: React.MouseEvent) => {
    e.preventDefault();
    nameRef.current?.focus();
    nameRef.current?.scrollIntoView?.({ block: 'center' });
  };
  const textBody =
    kind === 'companion-waiver' || kind === 'volunteer-waiver' ? (
      <PaperText kind={kind} text={text} headingLevel={3} signature={{ typed, printed: desk?.name, date: paperDate() }} />
    ) : (
      <MarkdownText text={text} headingLevel={3} />
    );

  return (
    <div className="docpanel" data-testid="agreement-sign">
      {own ? (
        <a href="#sign" className="go-sign" onClick={goSign} data-testid="agreement-go-sign">
          {t('agr.link.goSign')}
        </a>
      ) : null}
      <p className="eyebrow">{t('people.docEyebrow', { doc: title, version: doc.version })}</p>
      {doc.langs.length > 1 ? (
        <div className="seg" role="group" aria-label={t('agr.language')}>
          <button type="button" aria-pressed={lang === 'en'} onClick={() => setLang('en')} lang="en">
            English
          </button>
          <button type="button" aria-pressed={lang === 'es'} onClick={() => setLang('es')} lang="es">
            Español
          </button>
        </div>
      ) : start === 'es' || i18n.language === 'es' ? (
        <p className="help">{t('agr.englishOnly')}</p>
      ) : null}
      {doc.draft(lang) ? (
        <p className="warn" role="note">
          <Icon name="alert" /> <span>{t('people.needsReview')}</span>
        </p>
      ) : null}
      {own ? (
        <section className="trans doc-text large flow" aria-labelledby={`${uid}-text-h`} lang={lang} data-testid="agreement-sign-text">
          <h2 id={`${uid}-text-h`} className="agr-text-h">
            {t('agr.link.textHeading')}
          </h2>
          {textBody}
        </section>
      ) : (
        <div className="trans doc-text large" tabIndex={0} role="document" aria-label={t('agr.textLabel', { doc: title })} lang={lang} data-testid="agreement-sign-text">
          {textBody}
        </div>
      )}
      <div className="docbtns">
        <ReadAloud key={lang} text={text} lang={lang} testId="agreement-sign-read" />
      </div>

      <Box own={own} labelledBy={`${uid}-sign-h`}>
        {own ? (
          <h2 id={`${uid}-sign-h`} className="agr-sign-h">
            {t('agr.link.signHere')}
          </h2>
        ) : null}
        {desk ? <p className="help read-by">{t('agr.readBy', { name: desk.name, staff: desk.staff, date })}</p> : null}
        <div className="field">
          <label htmlFor={`${uid}-name`}>{desk ? t('agr.typedName') : t('agr.link.typedName')}</label>
          <input
            id={`${uid}-name`}
            className="input"
            ref={nameRef}
            type="text"
            inputMode="text"
            autoComplete={desk ? 'off' : 'name'}
            value={typed}
            maxLength={120}
            onChange={(e) => setTyped(e.target.value)}
            aria-invalid={problem === 'name' ? true : undefined}
            aria-describedby={[own ? `${uid}-name-hint` : '', problem === 'name' ? `${uid}-name-err` : ''].filter(Boolean).join(' ') || undefined}
            data-testid="agreement-sign-name"
          />
          {own ? (
            <p className="help" id={`${uid}-name-hint`}>
              {t('agr.link.nameHint')}
            </p>
          ) : null}
          {problem === 'name' ? (
            <p className="field-err" id={`${uid}-name-err`}>
              {t('agr.needName')}
            </p>
          ) : null}
        </div>
        <label className="switch" htmlFor={`${uid}-agree`}>
          <input id={`${uid}-agree`} ref={agreeRef} type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} aria-invalid={problem === 'agree' ? true : undefined} aria-describedby={problem === 'agree' ? `${uid}-agree-err` : undefined} data-testid="agreement-sign-agree" />
          <span>{desk ? t('agr.agree', { name: desk.name }) : t('agr.link.agree')}</span>
        </label>
        {problem === 'agree' ? (
          <p className="field-err" id={`${uid}-agree-err`}>
            {t('agr.needAgree')}
          </p>
        ) : null}
        {desk ? <p className="help">{t('agr.device')}</p> : null}
        <Button plain type="button" className={desk ? 'btn' : 'big-btn'} disabled={busy} aria-busy={busy} onClick={() => sign()} data-testid="agreement-sign-record">
          {desk ? (busy ? t('agr.recording') : t('agr.record')) : busy ? t('agr.link.signing') : t('agr.link.sign')}
        </Button>
        {error ? (
          <p className="field-err" role="alert" data-testid="agreement-sign-error">
            <Icon name="alert" width={16} height={16} /> {error}
          </p>
        ) : null}
      </Box>
    </div>
  );
}

/** The signing part: on the tenant's own page a section with its own heading and the id the "Go to the signature" link points to. */
function Box({ own, labelledBy, children }: { own: boolean; labelledBy: string; children: React.ReactNode }) {
  return own ? (
    <section className="signbox" id="sign" aria-labelledby={labelledBy} data-testid="agreement-sign-section">
      {children}
    </section>
  ) : (
    <div className="signbox">{children}</div>
  );
}
